import React, { Component } from 'react';
import FieldLevelHelp from './FieldLevelHelp';
import DiscountCode from './DiscountCode';
import {getBillingTypeDisplayName, formatCurrency, isUKOrder} from '../util/gearOrderUtil';

class PriceView extends Component {

    render() {
		let {
			showMaxOutOfPocketColumn,
			totalDueToday,
			totalDueTodayValue,
			displayTotalDue,
			physicianBillingSelected,
			isAmountDueToday,
			totalMaxOutOfPocket,
			feesByName,
			appliedDiscounts,
			requisitionPrices
		} = calculatePaymentBreakdown({
			gearOrder: this.props.gearOrder,
			prices: this.props.prices,
			doNotQuotePrices: this.props.doNotQuotePrices
		});

		let ukOrder = isUKOrder(this.props.gearOrder);
		const collectShippingChargesForUK = ukOrder && this.props.gearOrder?.shippingChargesUK && Object.keys(this.props.gearOrder.shippingChargesUK).length > 0
												&& Object.values(this.props.gearOrder.shippingChargesUK).reduce((sum, charge) => sum + charge, 0) > 0;
		
        if (this.props.configErrorFound) {
			totalDueToday = <FieldLevelHelp textClassName="text-warning" iconClassName="fa-exclamation-triangle" helpText="Unable to determine price." large="true" />;
            totalMaxOutOfPocket = <FieldLevelHelp textClassName="text-warning" iconClassName="fa-exclamation-triangle" helpText="Unable to determine price." large="true" />;
		} else if (this.props.allPricesLoaded) {
            if (this.props.gearOrder.insuranceRequiredForPayment) {
				totalDueToday = <FieldLevelHelp textClassName="text-warning" iconClassName="fa-exclamation-triangle" helpText="Price cannot be determined until the Insurance step is completed." large="true" />;
                totalMaxOutOfPocket = <FieldLevelHelp textClassName="text-warning" iconClassName="fa-exclamation-triangle" helpText="Price cannot be determined until the Insurance step is completed." large="true" />;
			} else {
				if(collectShippingChargesForUK)	{
					totalDueToday += Object.values(this.props.gearOrder.shippingChargesUK).reduce((sum, charge) => sum + charge, 0);					
				}
				isAmountDueToday = totalDueToday > 0;
				totalDueTodayValue = totalDueToday;
                totalDueToday = formatCurrency(totalDueToday, ukOrder);
				totalMaxOutOfPocket = formatCurrency("0.00", ukOrder) +" - " + formatCurrency(totalMaxOutOfPocket, ukOrder);
            }
        } else {
            totalDueToday = <i className='fa fa-spinner fa-spin fa-fw'></i>;
            totalMaxOutOfPocket = <i className='fa fa-spinner fa-spin fa-fw'></i>;
		}

		const showDiscountRow = !this.props.pageLocked && (physicianBillingSelected || isAmountDueToday);

		if (this.props.gearOrder.physicianInTheLoop) {
			return (
				<PitlPriceView
					requisitionPrices={requisitionPrices}
					totalDueToday={totalDueToday}
					totalDueTodayValue={totalDueTodayValue}
					feesByName={feesByName}
					discount={this.props.discount}
					applyDiscount={this.props.applyDiscount}
					pageLocked={this.props.pageLocked}
					pitlRecollectionOrderSummary={this.props.gearOrder.pitlRecollectionOrderSummary}
					gearOrder={this.props.gearOrder}
					appliedDiscounts={appliedDiscounts}
				/>
			);
		}

        return (
            <div>
                <table className="table table-striped">
                    <colgroup>
                        <col style={{width: "35%"}} />
                        <col style={{width: "20%"}} />
                        <col style={{width: "20%"}} />
                        {showMaxOutOfPocketColumn && <col style={{width: "21%"}} />}
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Requisition</th>
                            <th>Billing Option</th>
                            <th className="text-right">Due With Your Specimens</th>
                            {showMaxOutOfPocketColumn && <th className="text-right">Additional After Insurance</th>}
                        </tr>
                    </thead>
					<tbody>
						{requisitionPrices}
						{isUKOrder(this.props.gearOrder) && collectShippingChargesForUK
							&& (Object.keys(this.props.gearOrder.shippingChargesUK).map((key) => {
								const method = key;
								const charge = this.props.gearOrder.shippingChargesUK[key];
								return (
									<tr key={method}>
										<td colSpan={showMaxOutOfPocketColumn ? 3 : 2} className="text-right">
											<span className="text-info">Shipping Charge  {method}</span>
										</td>
										<td className="text-right">{formatCurrency(charge, true)}</td>
									</tr>
								);
							}))
						}
						{appliedDiscounts.map(discount => {
							return (
								<DiscountRow
									key={discount.discountCode}
									discount={discount}
									colCount={showMaxOutOfPocketColumn ? 4 : 3}
									gearOrder={this.props.gearOrder}
								/>
							);
						})}
                    </tbody>
                    <tfoot>
						{this.props.doNotQuotePrices ?
								<tr>
									<td colSpan="3" className="text-right">
										<small>Your practitioner has asked us to refer pricing questions to their office. Please contact your practitioner for payment amounts.</small>
									</td>
								</tr>
							:
								<>{(displayTotalDue || physicianBillingSelected) &&
									<>
									{showDiscountRow && <DiscountRow applyDiscount={this.props.applyDiscount} colCount={showMaxOutOfPocketColumn ? 4 : 3} gearOrder={this.props.gearOrder} />}
									{displayTotalDue &&
										<tr>
											<td colSpan="2" className="text-right"><strong>Total</strong></td>
											<td className="text-right"><strong>{totalDueToday}</strong></td>
											{showMaxOutOfPocketColumn && <td className="text-right"><strong>{totalMaxOutOfPocket}</strong></td>}
										</tr>
									}
									</>
								}</>
						}
                    </tfoot>
                </table>
            </div>
        );
    }
}

/**
 * Display the price table for a Physician in the Loop order.
 * This is very similar to the normal price table but PITL orders do not show the Billing Option or MaxOOP columns.
 */
const PitlPriceView = ({ requisitionPrices, totalDueToday, totalDueTodayValue, feesByName, discount, applyDiscount, pageLocked, pitlRecollectionOrderSummary, gearOrder, appliedDiscounts }) => {
	let paymentDescription = 'Due With Your Specimens';
	if (pitlRecollectionOrderSummary?.paymentRequired) {
		paymentDescription = 'Replacement Fee';
	}

	const showDiscountRow = !pageLocked && !pitlRecollectionOrderSummary;

	return (
		<div>
			<table className="table table-striped">
				<thead>
					<tr>
						<th>Requisition</th>
						<th className="text-right">{paymentDescription}</th>
					</tr>
				</thead>
				<tbody>{requisitionPrices}</tbody>
				<tfoot>
					<>
						{appliedDiscounts.map((discount) => (
							<DiscountRow
								key={discount.discountCode}
								discount={discount}
								colCount={2}
								gearOrder={gearOrder}
							/>
						))}
						{Object.keys(feesByName).map((key) => {
							const feeItem = feesByName[key];
							const isDiscount = feeItem?.price < 0;
							const className = isDiscount ? 'text-success' : '';
							const sign = isDiscount ? '-' : '';

							let description = <span className={className}>{feeItem.name}</span>
							const value = <span className={className}>{sign + formatCurrency(Math.abs(feeItem.price), isUKOrder(gearOrder))}</span>

							if (gearOrder?.physicianInTheLoop && feeItem.name === 'Physician Fee') {
								description = (
									<>
										<span className={className}>{feeItem.name}</span>
										<span onClick={() => window.open("https://connect.gdx.net/#faq", "_blank")}>
											<FieldLevelHelp
												textClassName="text-info"
												iconClassName="fa-info-circle"
												helpText="Fee covers physician authorization. Telehealth appointments to discuss results are available for an additional cost."
												trigger="hover"
												large={true}
												html={false}
											/>
										</span>
									</>
								);
							}

							return (
								<FeeRow
									key={feeItem.name}
									description={description}
									value={value}
									colCount={2}
								/>
							);
						})}
						{showDiscountRow && (
							<DiscountRow
								discount={discount}
								applyDiscount={applyDiscount}
								colCount={2}
								gearOrder={gearOrder}
								totalOrderAmountDue={totalDueTodayValue}
							/>
						)}
						<FeeRow
							key="total"
							description={<strong>Total</strong>}
							value={<strong>{totalDueToday}</strong>}
							colCount={2}
						/>
					</>
				</tfoot>
			</table>
		</div>
	);
};

const FeeRow = ({description, value, colCount}) => {
	return (
		<tr>
			<td colSpan={colCount} className="text-right">
				<span>{description}</span>
				<span style={{minWidth: '150px', display: 'inline-block'}}>{value}</span>
			</td>
		</tr>
	)
}

/**
 * Displays either the discount code input or the applied discount details.
 *
 * @param discount The current discount details
 * @param updateDiscount A function to pass validated discount details up to the parent
 */
const DiscountRow = ({ discount, applyDiscount, colCount, gearOrder, totalOrderAmountDue }) => {
	// Show the discount code input field if we don't already have a discount
	if (!discount) {
		// Only show the discount code input if all reqs are Patient Pay or Physician account billing
		if (
			gearOrder.requisitions?.every(
				(req) =>
					req.billingMethod === process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE ||
					req.billingMethod === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE
			)
		) {
			return (
				<tr>
					<td colSpan={colCount} className="text-right">
						<DiscountCode applyDiscount={applyDiscount} gearOrder={gearOrder} totalOrderAmountDue={totalOrderAmountDue} />
					</td>
				</tr>
			);
		}
		return null;
	}

	// Show the applied discount
	const name = discount?.discountCode;
	const description = <span className="text-success">Discount: {name}</span>
	const value = <span className="text-success">{discount?.discountAmount !== 0 ? ("-" + formatCurrency(discount?.discountAmount, isUKOrder(gearOrder))): ""}</span> // Displays nothing when the discountAmount is 0 because physician billing has no discounted amounts in gear

	return <FeeRow key={name} description={description} value={value} colCount={colCount} />
}


/**
 * The PriceView component expects a list of JSX components, and related values, but other components 
 * (like PayPal) need the actual data that builds the price view.
 * Separating the JSX from the data seemed like a daunting task, so this method will continue to build the
 * expected JSX while also building the paymentBreakown object. Everything is returned in a wrapper object 
 * and the caller can decide what information it cares about.
 */
const calculatePaymentBreakdown = ({ gearOrder, prices, doNotQuotePrices }) => {
	let paymentBreakdown = {
		items: [],
		discounts: [],
		fees: []
	};

	// only show the max oop column if one of the reqs is insurance billing
	// SCR-6524: Do not show max oop if the practitioner is in FL or NY
	let practitionerState = (((gearOrder.practitioner.office || {}).address || {}).state || '').toUpperCase();
	const insuranceProvider = gearOrder.insurance?.insuranceProvider;
	let showMaxOutOfPocketColumn =
		gearOrder.requisitions.some((req) => req.insuranceBillingSelected) &&
		insuranceProvider !== 'medicare' &&
		insuranceProvider !== 'medicaid' &&
		insuranceProvider !== 'tricare' &&
		insuranceProvider !== 'medicare_railroad' &&
		practitionerState !== 'NY' &&
		practitionerState !== 'FL';

	let totalDueToday = 0;
	let totalDueTodayValue = 0;
	let displayTotalDue = false;
	let physicianBillingSelected = gearOrder?.requisitions?.some(
		(req) => req.billingMethod === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE
	);
	// Is total due today > $0
	let isAmountDueToday = false;
	let totalMaxOutOfPocket = 0;
	let feesByName = {};
	let appliedDiscounts = [];
	let shippingFeeIncluded = false;

	const ukOrder = isUKOrder(gearOrder);
	const ukInsuranceOrder = ukOrder && gearOrder?.requisitions?.every(req => req?.billingMethod === process.env.REACT_APP_BILLING_ONLY_INSURANCE_VALUE);

	let requisitionPrices = gearOrder.requisitions.map((requisition, reqIndex) => {
		let dueToday = null;
		let maxOutOfPocket = null;
		if (doNotQuotePrices) {
			dueToday = (
				<FieldLevelHelp
					textClassName="text-info"
					iconClassName="fa-info-circle"
					helpText="Your practitioner has asked us to refer pricing questions to their office. Please contact your practitioner for payment amounts."
					large="true"
				/>
			);
			maxOutOfPocket = '';
			paymentBreakdown.error = 'doNotQuotePrices';
		} else if (!requisition.billingMethod) {
			dueToday = (
				<FieldLevelHelp
					textClassName="text-info"
					iconClassName="fa-info-circle"
					helpText="We cannot estimate a price because the billing option is unknown. Please contact your practitioner or call us at {getCustomerCareNumber(this.props?.gearOrder)} for any questions about pricing and payment amounts."
					large="true"
				/>
			);
			maxOutOfPocket = (
				<FieldLevelHelp
					textClassName="text-info"
					iconClassName="fa-info-circle"
					helpText="We cannot estimate a price because the billing option is unknown. Please contact your practitioner or call us at {getCustomerCareNumber(this.props?.gearOrder)} for any questions about pricing and payment amounts."
					large="true"
				/>
			);
			paymentBreakdown.error = 'billingMethodRequired';
		} else if (requisition.physicianBillingSelected) {
			dueToday = (
				<FieldLevelHelp
					textClassName="text-info"
					iconClassName="fa-info-circle"
					helpText="Physician account billing selected."
					large="true"
				/>
			);
			maxOutOfPocket = (
				<FieldLevelHelp
					textClassName="text-info"
					iconClassName="fa-info-circle"
					helpText="Physician account billing selected."
					large="true"
				/>
			);

			let price = prices.find((price) => price.gearRequisitionId === requisition.id);
			if (price?.discount?.discountCode) {
				appliedDiscounts.push(price.discount);
			}
		} else {
			displayTotalDue = true;
			let price = prices.find((price) => price.gearRequisitionId === requisition.id);
			let insuranceRequiredForPayment =
				gearOrder.medicareBillingNoInsurance &&
				requisition.medicareTricareBillingSelected &&
				price &&
				price.medicarePrice !== price.tricarePrice;
			if (price && price.error) {
				dueToday = (
					<FieldLevelHelp
						textClassName="text-warning"
						iconClassName="fa-exclamation-triangle"
						helpText="Unable to determine price."
						large="true"
					/>
				);
				maxOutOfPocket = (
					<FieldLevelHelp
						textClassName="text-warning"
						iconClassName="fa-exclamation-triangle"
						helpText="Unable to determine price."
						large="true"
					/>
				);
				paymentBreakdown.error = 'priceError';
			} else if (price && price.placeholder) {
				dueToday = <i className="fa fa-spinner fa-spin fa-fw"></i>;
				maxOutOfPocket = <i className="fa fa-spinner fa-spin fa-fw"></i>;
				paymentBreakdown.error = 'loading';
			} else if (insuranceRequiredForPayment) {
				dueToday = (
					<FieldLevelHelp
						textClassName="text-warning"
						iconClassName="fa-exclamation-triangle"
						helpText="Price cannot be determined until the Insurance step is completed."
						large="true"
					/>
				);
				maxOutOfPocket = (
					<FieldLevelHelp
						textClassName="text-warning"
						iconClassName="fa-exclamation-triangle"
						helpText="Price cannot be determined until the Insurance step is completed."
						large="true"
					/>
				);
				paymentBreakdown.error = 'insuranceRequiredForPayment';
			} else if (price) {
				// Do not display a price for $0 PITL orders
				if (requisition.physicianInTheLoop && !price?.price) {
					return null;
				}

				paymentBreakdown.items.push({
					gearReqId: requisition.id,
					omsOrderableItemName: requisition.omsOrderableItemName,
					panelName: requisition.panelName,
					omsOrderableItemId: requisition.omsOrderableItemId,
					price: ukInsuranceOrder ? 0 : price.price
				});

				totalDueToday = totalDueToday + (ukInsuranceOrder ? 0 : price.price);
				dueToday = formatCurrency(ukInsuranceOrder ? 0 : price.price, isUKOrder(gearOrder));
				let discountAmount = price.discount?.discountAmount || 0;
				if (price.discount?.discountAmount || price.discount?.discountType === 'PRICE') {
					if (price.discount.discountType === 'PRICE') {
						let physicianFee = 0;
						if (requisition.physicianInTheLoop) {
							// Up to $15 of the payment will go to the Physician Fee. The rest is the discount.
							physicianFee =
								price.items?.find((item) => item.feeItem && item.name === 'Physician Fee')?.price || 0;
						}
						discountAmount = Math.max(
							price.price - Math.max(price.discount.discountAmount - physicianFee, 0),
							0
						);
					}
					totalDueToday = totalDueToday - discountAmount;

					// If the same discount is applied to multiple reqs, combine the amounts into one line item
					const existingDiscountIndex = appliedDiscounts.findIndex(
						(discount) => discount.discountCode === price.discount.discountCode
					);
					if (existingDiscountIndex >= 0) {
						appliedDiscounts[existingDiscountIndex].discountAmount += discountAmount;
					} else {
						appliedDiscounts.push({ ...price.discount, discountAmount: discountAmount });
					}
				}

				if (price?.maxOutOfPocket > price.price) {
					// SCR-7376: Calculate as (maxOOP - due Today)
					const additionalAfterInsurance = price.maxOutOfPocket - price.price;
					maxOutOfPocket =
						formatCurrency(0.00, isUKOrder(gearOrder)) + " - " + formatCurrency(additionalAfterInsurance, isUKOrder(gearOrder));
					totalMaxOutOfPocket = totalMaxOutOfPocket + additionalAfterInsurance;
				} else {
					//no maxOOP defined
					maxOutOfPocket = 'N/A';
				}

				// Add any fees to the total and create a new total row item for each type of fee
				if (price.items?.some((item) => item.feeItem)) {
					price.items
						.filter((item) => item.feeItem)
						.forEach((item) => {
							// The PITL physician fee should only be added once for the whole order
							/*
							 * The PITL shipping fee is already included in the req prices, but
							 * needs to be subtracted from the total for every req except the first
							 * one (only charge the fee once but show a discount for all additional
							 * shipping fees)
							 */
							const existingFee = feesByName[item.name];

							if (requisition.physicianInTheLoop) {
								if (item.name === 'Shipping & Handling Fee') {
									const priceMinusDiscount = (price.price || 0) - (discountAmount || 0);
									const displayName = 'Multiple Product Discount';
									/**
									 * If the shipping fee has already been included as a charge on one of the reqs and this
									 * req is valid for shipping charges, subtract the fee from the total price and update the
									 * Multi-product discount value.
									 */
									if (shippingFeeIncluded && priceMinusDiscount >= item.price) {
										totalDueToday = totalDueToday - item.price;
										feesByName[item.name] = {
											name: displayName,
											price: (existingFee?.price ?? 0) - item.price
										};
									}
									/**
									 * The back-end only adds the shipping fee if the charge amount is greater than or equal to the shipping fee.
									 * This is to account for 100% discount codes.
									 * This logic does not apply to affiliate payments.
									 */
									if (
										priceMinusDiscount >= item.price ||
										price.discount?.discountType === 'AFFILIATE' ||
										requisition.paymentInfo?.paymentFrom === 'affiliate'
									) {
										shippingFeeIncluded = true;
									}
								} else {
									/**
									 * The total Physician Fee is only charged once, but 'PRICE' type
									 * discounts could cause the total to come from a sum of partial fees.
									 * Add up any new amount until the total fee amount is reached.
									 */
									const maxFee = item.price;
									const currentFeeAmount = existingFee?.price ?? 0;
									let additionalFee = Math.max(0, maxFee - currentFeeAmount);
									if (price.discount?.discountType === 'PRICE') {
										additionalFee = Math.min(additionalFee, price.discount.discountAmount || 0);
									}

									if (additionalFee > 0) {
										let totalFee = Math.min(maxFee, currentFeeAmount + additionalFee);
										totalDueToday = totalDueToday + additionalFee;

										feesByName[item.name] = {
											name: item.name,
											price: totalFee
										};
									}
								}
							} else {
								totalDueToday = totalDueToday + item.price;

								feesByName[item.name] = {
									name: item.name,
									price: (existingFee?.price ?? 0) + item.price
								};
							}
						});
				}

				/**
				 * Showing an International Surcharge line item was proving difficult so I
				 * decided to add an info icon to note the fee.
				 */
				const internationalSurchargeItem = price.items?.find((item) => item.name === 'International Surcharge');
				if (internationalSurchargeItem) {
					dueToday = (
						<>
							{dueToday}
							<FieldLevelHelp
								textClassName="text-info"
								iconClassName="fa-info-circle"
								helpText={'The price includes a ' + formatCurrency(internationalSurchargeItem.price, isUKOrder(gearOrder)) + ' International Surcharge.'}
								large="true"
								trigger="hover"
							/>
						</>
					);
				}
			} else if (requisition.physicianInTheLoop && requisition.accessionNo) {
				// Do not display a price for accessioned PITL requisitions
				return null;
			}
		}
		return (
			<tr key={requisition.id}>
				<td>{requisition.omsOrderableItemName}</td>
				{gearOrder.physicianInTheLoop ? (
					<td className="text-right">{dueToday}</td>
				) : (
					<>
						<td>{getBillingTypeDisplayName(requisition.billingMethod) || 'Unknown'}</td>
						<td className="text-right">{dueToday}</td>
						{showMaxOutOfPocketColumn && <td className="text-right">{maxOutOfPocket}</td>}
					</>
				)}
			</tr>
		);
	});

	paymentBreakdown.discounts = Object.values({ ...appliedDiscounts });
	paymentBreakdown.fees = Object.values({ ...feesByName });
	paymentBreakdown.total = totalDueToday;

	return {
		paymentBreakdown,
		showMaxOutOfPocketColumn,
		totalDueToday,
		totalDueTodayValue,
		displayTotalDue,
		physicianBillingSelected,
		isAmountDueToday,
		totalMaxOutOfPocket,
		feesByName,
		appliedDiscounts,
		requisitionPrices
	};
};

export default PriceView;
export { calculatePaymentBreakdown };
